import { setShowAIAnalyst } from '@/app/ai/atoms/aiAnalystAtoms';
import { events } from '@/app/events/events';

// Opens the AI Analyst panel and runs `callback` once the chat input is
// mounted and has subscribed to its events.
//
// If the chat input is already in the DOM, we defer `callback` via a
// microtask. This is important when the helper is invoked from a sibling's
// `useEffect` in the same commit as the AI panel mounts: React runs effects
// in DFS post-order, and event emits issued synchronously from a parent
// effect can fire before a child's subscription effect has registered.
// Microtask scheduling pushes execution past the current effect batch so
// child subscriptions are in place.
//
// If the chat input is NOT in the DOM, we wait for the `aiAnalystReady`
// event. This covers two cases: (a) `setShowAIAnalyst(true)` was just
// called and the panel hasn't rendered yet, and (b) the panel is gated on
// permissions/auth (see `<AIAnalyst />` in `QuadraticUI`) which may still
// be loading on a cold file open — the atom can be true while the
// component isn't mounted yet.
//
// Returns a cleanup function suitable for use directly from a `useEffect`.
export const openAIAnalystAndRun = (callback: () => void): (() => void) => {
  setShowAIAnalyst(true);

  const isChatInputMounted =
    typeof document !== 'undefined' && document.querySelector('[data-ai-analyst-input]') !== null;

  if (isChatInputMounted) {
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) callback();
    });
    return () => {
      cancelled = true;
    };
  }

  const handleReady = () => {
    events.off('aiAnalystReady', handleReady);
    callback();
  };
  events.on('aiAnalystReady', handleReady);
  return () => events.off('aiAnalystReady', handleReady);
};
